// The Pixi world under the coop screen.
//
// Two things make it the v1 version of the world rather than the desktop one:
// the camera is fitted to the diorama box and re-asserted every frame, so a
// stray drag moves nothing (REFOCUS §4: one composed scene, no pan or zoom), and
// the ticker stops when the stage is fully covered by a screen, which is most of
// the battery on a phone (PLAN-03 §4).
//
// Nothing under src/render/ is edited: the renderer is used as it is, and the
// fit and the lock live here.

import type { GameStateStore } from '../core/store';
import type { GameState } from '../core/types';
import { WorldRenderer } from '../render/worldRenderer';
import { assets } from '../render/assetLoader';
import { loadSpriteMeta } from '../render/spriteMeta';
import { SPRITE_ROOT, preloadList } from '../render/spriteManifest';
import { TILE_DEPTH, TILE_HEIGHT, TILE_WIDTH, tileToWorld } from '../render/iso';

/** Fraction of the box the yard fills across, and down. */
const FIT_MARGIN_X = 0.98;
const FIT_MARGIN_Y = 0.92;
/** Coops and the brooder lamp stand above their tile. */
const FIT_HEADROOM_PX = 150;

export interface CoopWorldOptions {
  onBirdTap: (chickenId: string) => void;
  onTileTap?: (x: number, y: number) => void;
}

export class CoopWorld {
  private renderer: WorldRenderer | null = null;
  private fitted = { x: 0, y: 0, zoom: 1 };
  private active = true;
  private boxWidth = 0;
  private boxHeight = 0;
  private boxX = 0;
  private boxY = 0;
  private disposed = false;

  constructor(
    private readonly host: HTMLElement,
    private readonly store: GameStateStore,
    private readonly options: CoopWorldOptions,
  ) {}

  async mount(): Promise<void> {
    const renderer = new WorldRenderer(this.host, {
      onChickenTap: (id) => this.options.onBirdTap(id),
      onTileTap: (tile) => this.options.onTileTap?.(tile.x, tile.y),
    });
    this.renderer = renderer;
    // The sky above the yard is the canvas's own background rather than a div
    // painted over it. The colour comes from the token, read at runtime, so
    // theme.css stays the only place a colour is written down.
    const sky = getComputedStyle(document.documentElement)
      .getPropertyValue('--cf-sky')
      .trim();
    if (sky) renderer.app.renderer.background.color = Number.parseInt(sky.slice(1), 16);
    // The diorama is a rounded box; the canvas has to be too.
    const canvas = renderer.app.view as unknown as HTMLCanvasElement;
    canvas.style.borderRadius = '24px';
    renderer.setState(this.store.getState());
    // Geometry before pixels, the same order src/main.ts boots in: a texture
    // that lands before its metadata is placed once and never re-measured.
    await loadSpriteMeta(SPRITE_ROOT);
    void assets.preload(preloadList()).then(() => {
      if (this.disposed) return;
      renderer.setState(this.store.getState());
    });
    await renderer.mount();
    this.fit();
    // The lock, not a flag inside the renderer: whatever the pointer does, the
    // camera is put back before the frame is drawn.
    renderer.onEachFrame(() => this.refit());
  }

  /**
   * Sizes the world to a box in the layout and fits the yard inside it. Called
   * whenever the coop screen lays out, and again on a rotation.
   */
  setBox(x: number, y: number, width: number, height: number): void {
    if (this.boxWidth === width && this.boxHeight === height && this.boxX === x && this.boxY === y) {
      return;
    }
    this.boxX = x;
    this.boxY = y;
    this.boxWidth = width;
    this.boxHeight = height;
    const renderer = this.renderer;
    if (!renderer) return;
    // The canvas is placed over the screen's diorama box rather than filling
    // the column: the stage is a full-height background layer and the yard is
    // one composed scene in the middle of it.
    this.host.style.transform = `translate(${x}px, ${y}px)`;
    // resizeTo: the host sizes the canvas on window resize only, so the app's
    // own box change has to say so.
    renderer.app.renderer.resize(width, height);
    renderer.camera.resize(width, height);
    this.fit();
  }

  /** False stops the renderer. The sim keeps ticking — that is the clock's job. */
  setActive(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    const renderer = this.renderer;
    if (!renderer) return;
    if (active) {
      renderer.app.ticker.start();
      this.fit();
    } else {
      renderer.app.ticker.stop();
    }
  }

  setState(state: Readonly<GameState>): void {
    this.renderer?.setState(state);
    this.fit();
  }

  private fit(): void {
    const renderer = this.renderer;
    const state = this.store.getState();
    if (!renderer || this.boxWidth <= 0 || this.boxHeight <= 0) return;
    const { width, height } = state.grid;
    // The grid is an iso diamond, so its extent is (width + height) half-tiles
    // on each axis; whichever axis is tighter sets the zoom.
    const spanX = (width + height) * (TILE_WIDTH / 2);
    const spanY = (width + height) * (TILE_HEIGHT / 2) + FIT_HEADROOM_PX;
    const zoom = Math.min(
      (this.boxWidth * FIT_MARGIN_X) / spanX,
      (this.boxHeight * FIT_MARGIN_Y) / spanY,
    );
    const centre = tileToWorld(width / 2, height / 2);
    this.fitted = {
      x: centre.x,
      // The headroom belongs above the yard, so the composition is centred on
      // the ground rather than on the box that includes it. Half a tile down
      // puts the middle of the rhombus, not its top corner, in the middle.
      y: centre.y + TILE_HEIGHT / 2 - FIT_HEADROOM_PX / 2,
      zoom,
    };
    this.refit();
  }

  private refit(): void {
    const camera = this.renderer?.camera;
    if (!camera) return;
    const { x, y, zoom } = this.fitted;
    if (camera.x !== x || camera.y !== y || camera.zoom !== zoom) {
      camera.centerOn(x, y);
      camera.setZoom(zoom);
    }
  }

  /** Screen-space position of a bird, for the spot markers over the diorama. */
  screenPointOf(chickenId: string): { x: number; y: number } | null {
    const renderer = this.renderer;
    if (!renderer) return null;
    const bird = this.store.getState().flock.find((c) => c.id === chickenId);
    if (!bird) return null;
    const p = tileToWorld(
      bird.position.x + 0.5,
      bird.position.y + 0.5,
      bird.position.tileZ,
    );
    return renderer.camera.worldToScreen(p.x, p.y + TILE_HEIGHT / 2 - TILE_DEPTH * 2);
  }

  destroy(): void {
    this.disposed = true;
    this.renderer?.destroy();
    this.renderer = null;
  }
}
